import React, { Fragment, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createSocket } from '../socket';
import { AuthContext } from '../context/authContext';
import { useParams, useNavigate } from 'react-router-dom';
import alertService from "../services/sweetAlert";
import './Loka.css';

const cdn = 'https://files.geetsuhane.com/';

// ============================================
// VISHNU LOKA LOCATIONS
// ============================================
const VISHNU_LOCATIONS = [
  {
    id: 'vaikuntha',
    name: 'वैकुंठ धाम',
    nameEn: 'Vaikuntha - Divine Abode',
    background: `${cdn}loka/vishnu5.gif`,
    description: 'श्री विष्णु का दिव्य निवास, जहां सदा शांति और आनंद का वास है',
    elements: ['lotus', 'particles', 'chakra'],
    blessing: 'ॐ नमो नारायणाय 🙏'
  },
  {
    id: 'ksheer-sagar',
    name: 'क्षीर सागर',
    nameEn: 'Ocean of Milk',
    background: `${cdn}loka/ksheer-sagar.jpg`,
    description: 'अनंत शेषनाग पर विराजमान श्री विष्णु, जहां लक्ष्मी माता सेवा में हैं',
    elements: ['waves', 'serpent', 'particles'],
    blessing: 'शान्ताकारं भुजगशयनं पद्मनाभं सुरेशम् 🙏'
  },
  {
    id: 'garuda-mandap',
    name: 'गरुड़ मंडप',
    nameEn: 'Garuda Sanctuary',
    background: `${cdn}loka/garuda.jpg`,
    description: 'दिव्य गरुड़ का निवास, विष्णु भगवान का वाहन',
    elements: ['feathers', 'wind', 'particles'],
    blessing: 'गरुडध्वजाय विद्महे 🦅'
  },
  {
    id: 'padma-van',
    name: 'पद्म वन',
    nameEn: 'Divine Lotus Garden',
    background: `${cdn}loka/padma-van.jpg`,
    description: 'दिव्य कमलों का वन, जहां से ब्रह्मा जी की उत्पत्ति हुई',
    elements: ['lotus', 'butterflies', 'particles', 'flowers'],
    blessing: 'पद्मनाभाय नमः 🪷'
  }
];

// ============================================
// SHIV LOKA LOCATIONS
// ============================================
const SHIV_LOCATIONS = [
  {
    id: 'kailash',
    name: 'कैलाश पर्वत',
    nameEn: 'Mount Kailash',
    background: `${cdn}loka/shiva.jpeg`,
    description: 'भोलेनाथ का दिव्य निवास, जहां शिव-पार्वती विराजमान हैं',
    elements: ['snow', 'particles', 'trishul'],
    blessing: 'ॐ नमः शिवाय 🔱'
  },
  {
    id: 'ganga-dhara',
    name: 'गंगा धारा',
    nameEn: 'Sacred Ganga Flow',
    background: `${cdn}loka/ganga.jpg`,
    description: 'शिव की जटाओं से निकलती पवित्र गंगा माता',
    elements: ['water', 'ganga', 'particles'],
    blessing: 'गंगे च यमुने चैव गोदावरि सरस्वति 🙏'
  },
  {
    id: 'nandi-mandap',
    name: 'नंदी मंडप',
    nameEn: 'Nandi Temple',
    background: `${cdn}loka/nandi.jpg`,
    description: 'भगवान शिव के प्रिय वाहन नंदी का मंदिर',
    elements: ['bells', 'particles', 'om'],
    blessing: 'नंदीश्वराय नमः 🐂'
  },
  {
    id: 'dhyan-gufa',
    name: 'ध्यान गुफा',
    nameEn: 'Meditation Cave',
    background: `${cdn}loka/cave.jpg`,
    description: 'महादेव की तपस्थली, जहां अनंत ध्यान में लीन हैं',
    elements: ['incense', 'om', 'particles'],
    blessing: 'ध्यायेत्सततं महेशम् 🧘'
  }
];

// ============================================
// BRAHM LOKA LOCATIONS
// ============================================
const BRAHM_LOCATIONS = [
  {
    id: 'satya-loka',
    name: 'सत्य लोक',
    nameEn: 'Brahma Abode',
    background: `${cdn}loka/bramha.jpeg`,
    description: 'ब्रह्मा जी का दिव्य निवास, सृष्टि के रचयिता का धाम',
    elements: ['cosmos', 'particles', 'vedas'],
    blessing: 'ॐ ब्रह्मणे नमः 🙏'
  },
  {
    id: 'saraswati-teer',
    name: 'सरस्वती तीर',
    nameEn: 'Saraswati Banks',
    background: `${cdn}loka/saraswati.jpg`,
    description: 'माता सरस्वती का निवास, विद्या और संगीत की देवी',
    elements: ['veena', 'books', 'particles', 'swans'],
    blessing: 'या कुन्देन्दुतुषारहारधवला 🎵'
  },
  {
    id: 'ved-bhandar',
    name: 'वेद भंडार',
    nameEn: 'Library of Vedas',
    background: `${cdn}loka/vedas.jpg`,
    description: 'चारों वेदों का दिव्य भंडार, ज्ञान का अनंत सागर',
    elements: ['scrolls', 'light', 'particles'],
    blessing: 'वेदों का ज्ञान तुम्हें प्राप्त हो 📜'
  },
  {
    id: 'padma-srishti',
    name: 'पद्म सृष्टि',
    nameEn: 'Cosmic Lotus',
    background: `${cdn}loka/cosmic-lotus.jpg`,
    description: 'विष्णु नाभि से उत्पन्न दिव्य कमल, जहां से सृष्टि प्रारंभ हुई',
    elements: ['lotus', 'cosmos', 'particles', 'stars'],
    blessing: 'सृष्टि के आदि स्रोत को नमन 🪷✨'
  }
];

// ============================================
// BLESSINGS FOR EACH LOKA
// ============================================
const VISHNU_BLESSINGS = [
  "ॐ नमो नारायणाय 🙏",
  "सर्वं विष्णुमयं जगत् - यह सारा जगत विष्णुमय है",
  "शान्ताकारं भुजगशयनं पद्मनाभं सुरेशम् 🙏",
  "मेरा आशीर्वाद तुम्हारे साथ है, वत्स 🙏",
  "धर्म की रक्षा करो, सत्य का पालन करो",
  "ॐ विष्णवे नमः - तुम्हारा कल्याण हो 🙏",
  "जब जब धर्म की हानि होती है, मैं अवतार लेता हूं",
  "शरणागत की रक्षा मेरा धर्म है 🙏",
  "तुम्हारी भक्ति से मैं प्रसन्न हूं, वत्स",
  "ॐ नमो भगवते वासुदेवाय 🙏"
];

const SHIV_BLESSINGS = [
  "ॐ नमः शिवाय 🙏",
  "हर हर महादेव! 🔱",
  "मैं भोलेनाथ हूं, सबका कल्याण करता हूं",
  "त्रिशूल धारी, नीलकंठ तुम्हें आशीर्वाद देते हैं 🙏",
  "जटाधारी शंकर का आशीर्वाद तुम्हारे साथ है",
  "ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम् 🙏",
  "शिव शक्ति का मिलन ही सृष्टि है",
  "भस्म भूषित, नाग धारी महादेव की जय 🔱",
  "कर्पूरगौरं करुणावतारं 🙏",
  "शंभो महादेव! 🔱"
];

const BRAHMA_BLESSINGS = [
  "ॐ ब्रह्मणे नमः 🙏",
  "सृष्टि के रचयिता का आशीर्वाद तुम्हारे साथ है",
  "ज्ञान ही परम शक्ति है, वत्स",
  "चतुर्मुख ब्रह्मा तुम्हें विद्या का वरदान देते हैं 🙏",
  "वेदों का ज्ञान तुम्हें प्राप्त हो",
  "सरस्वती माता का आशीर्वाद तुम्हारे साथ है 🙏",
  "सत्यं ज्ञानं अनंतं ब्रह्म 📿",
  "विद्या ददाति विनयम् 🙏"
];

export default function Loka() {
  const { currentUser } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [taggedUser, setTaggedUser] = useState('');
  const { room } = useParams();
  const socket = createSocket();
  const navigate = useNavigate();

  // Loka state
  const [lokaTheme, setLokaTheme] = useState('vishnu');
  const [locations, setLocations] = useState(VISHNU_LOCATIONS);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showBlessings, setShowBlessings] = useState(false);
  const [currentBlessing, setCurrentBlessing] = useState('');
  const [particles, setParticles] = useState([]);
  const [showLocationInfo, setShowLocationInfo] = useState(true);
  const [diyaLit, setDiyaLit] = useState(false);
  const [bellRinging, setBellRinging] = useState(false);
  const [flowersOffered, setFlowersOffered] = useState(0);

  // Audio refs
  const ambientAudioRef = useRef(null);
  const bellAudioRef = useRef(null);
  const transitionAudioRef = useRef(null);

  // WebRTC refs
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerConnections = useRef({});
  const localStream = useRef(null);

  // Current location
  const currentLocation = locations[currentLocationIndex];

  // Get blessings based on loka
  const getBlessings = useCallback(() => {
    switch (lokaTheme) {
      case 'vishnu': return VISHNU_BLESSINGS;
      case 'shiv': return SHIV_BLESSINGS;
      case 'bramh': return BRAHMA_BLESSINGS;
      default: return VISHNU_BLESSINGS;
    }
  }, [lokaTheme]);

  // Set loka theme and locations based on room
  useEffect(() => {
    const roomLower = room?.toLowerCase();
    switch (roomLower) {
      case 'vishnu':
        setLokaTheme('vishnu');
        setLocations(VISHNU_LOCATIONS);
        break;
      case 'shiv':
        setLokaTheme('shiv');
        setLocations(SHIV_LOCATIONS);
        break;
      case 'bramh':
        setLokaTheme('bramh');
        setLocations(BRAHM_LOCATIONS);
        break;
      default:
        setLokaTheme('vishnu');
        setLocations(VISHNU_LOCATIONS);
    }
    setCurrentLocationIndex(0);
  }, [room]);

  // Generate particles based on location elements
  useEffect(() => {
    const generateParticles = () => {
      const count = currentLocation?.elements?.includes('cosmos') ? 50 : 30;
      const newParticles = Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 5,
        duration: 5 + Math.random() * 5,
        size: 3 + Math.random() * 5,
        type: currentLocation?.elements?.[Math.floor(Math.random() * currentLocation?.elements?.length)] || 'particle'
      }));
      setParticles(newParticles);
    };
    generateParticles();
  }, [currentLocation]);

  // Show periodic blessings
  useEffect(() => {
    const blessings = getBlessings();
    const showBlessing = () => {
      const randomBlessing = blessings[Math.floor(Math.random() * blessings.length)];
      setCurrentBlessing(randomBlessing);
      setShowBlessings(true);
      setTimeout(() => setShowBlessings(false), 5000);
    };

    const initialTimeout = setTimeout(showBlessing, 3000);
    const interval = setInterval(showBlessing, 25000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [getBlessings, currentLocationIndex]);

  // Hide location info after 5 seconds
  useEffect(() => {
    setShowLocationInfo(true);
    const timeout = setTimeout(() => setShowLocationInfo(false), 5000);
    return () => clearTimeout(timeout);
  }, [currentLocationIndex]);

  // Navigate to next location
  const goToNextLocation = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // Play transition sound
    if (transitionAudioRef.current && audioEnabled) {
      transitionAudioRef.current.src = cdn + 'loka/whoosh.mp3';
      transitionAudioRef.current.volume = 0.5;
      transitionAudioRef.current.play().catch(() => {});
    }

    setTimeout(() => {
      setCurrentLocationIndex(prev => (prev + 1) % locations.length);
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, locations.length, audioEnabled]);

  // Navigate to previous location
  const goToPrevLocation = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    if (transitionAudioRef.current && audioEnabled) {
      transitionAudioRef.current.src = cdn + 'loka/whoosh.mp3';
      transitionAudioRef.current.volume = 0.5;
      transitionAudioRef.current.play().catch(() => {});
    }

    setTimeout(() => {
      setCurrentLocationIndex(prev => prev === 0 ? locations.length - 1 : prev - 1);
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, locations.length, audioEnabled]);

  // Jump to specific location
  const goToLocation = useCallback((index) => {
    if (isTransitioning || index === currentLocationIndex) return;
    setIsTransitioning(true);

    if (transitionAudioRef.current && audioEnabled) {
      transitionAudioRef.current.src = cdn + 'loka/whoosh.mp3';
      transitionAudioRef.current.volume = 0.5;
      transitionAudioRef.current.play().catch(() => {});
    }

    setTimeout(() => {
      setCurrentLocationIndex(index);
      setIsTransitioning(false);
    }, 500);
  }, [isTransitioning, currentLocationIndex, audioEnabled]);

  // Auto-rotate through locations every 12 seconds
  useEffect(() => {
    const autoRotateInterval = setInterval(() => {
      if (!isTransitioning) {
        goToNextLocation();
      }
    }, 12000);

    return () => clearInterval(autoRotateInterval);
  }, [goToNextLocation, isTransitioning]);

  // Enable audio
  const enableAudio = useCallback(() => {
    if (audioEnabled) return;
    setAudioEnabled(true);

    if (ambientAudioRef.current) {
      const mantraUrl = lokaTheme === 'vishnu'
        ? cdn + 'loka/om-namo-narayanaya.mp3'
        : lokaTheme === 'shiv'
        ? cdn + 'loka/om-namah-shivaya.mp3'
        : cdn + 'loka/gayatri-mantra.mp3';

      ambientAudioRef.current.src = mantraUrl;
      ambientAudioRef.current.volume = 0.3;
      ambientAudioRef.current.loop = true;
      ambientAudioRef.current.play().catch(() => {});
    }
  }, [audioEnabled, lokaTheme]);

  // Ring bell
  const ringBell = useCallback(() => {
    setBellRinging(true);

    if (bellAudioRef.current && audioEnabled) {
      bellAudioRef.current.src = cdn + 'loka/temple-bell.mp3';
      bellAudioRef.current.volume = 0.7;
      bellAudioRef.current.play().catch(() => {});
    }

    const blessings = getBlessings();
    setCurrentBlessing(blessings[Math.floor(Math.random() * blessings.length)]);
    setShowBlessings(true);

    setTimeout(() => {
      setBellRinging(false);
      setShowBlessings(false);
    }, 3000);

    socket.emit('sendMessage', {
      room,
      message: '🔔 घंटी बजाई',
      userId: currentUser?.SID,
      name: currentUser?.name,
      picture: currentUser?.picture,
      isAction: true
    });
  }, [audioEnabled, getBlessings, room, currentUser, socket]);

  // Light diya
  const lightDiya = useCallback(() => {
    setDiyaLit(true);

    socket.emit('sendMessage', {
      room,
      message: '🪔 दीया जलाया',
      userId: currentUser?.SID,
      name: currentUser?.name,
      picture: currentUser?.picture,
      isAction: true
    });

    setTimeout(() => setDiyaLit(false), 60000);
  }, [room, currentUser, socket]);

  // Offer flowers
  const offerFlowers = useCallback(() => {
    setFlowersOffered(prev => prev + 1);

    const blessings = getBlessings();
    setCurrentBlessing(blessings[Math.floor(Math.random() * blessings.length)]);
    setShowBlessings(true);
    setTimeout(() => setShowBlessings(false), 4000);

    socket.emit('sendMessage', {
      room,
      message: '🌺 पुष्प अर्पित किए',
      userId: currentUser?.SID,
      name: currentUser?.name,
      picture: currentUser?.picture,
      isAction: true
    });
  }, [getBlessings, room, currentUser, socket]);

  // Pranam
  const doPranam = useCallback(() => {
    const blessings = getBlessings();
    setCurrentBlessing(blessings[Math.floor(Math.random() * blessings.length)]);
    setShowBlessings(true);
    setTimeout(() => setShowBlessings(false), 5000);

    socket.emit('sendMessage', {
      room,
      message: '🙏 प्रणाम किया',
      userId: currentUser?.SID,
      name: currentUser?.name,
      picture: currentUser?.picture,
      isAction: true
    });
  }, [getBlessings, room, currentUser, socket]);

  // Socket and authorization
  useEffect(() => {
    if (room && currentUser) {
      const roomLower = room.toLowerCase();
      const gname = currentUser?.gname;
      const isAdmin = currentUser?.SID === '008';

      // Authorization check
      if (roomLower === 'vishnu') {
        if (!['Rockers', 'Tigers', 'Global Stars'].includes(gname) && !isAdmin) {
          alertService.info('Not authorized', 'Only Rockers, Tigers and Global Stars are allowed in Vishnu Loka');
          navigate('/gupshup/' + gname);
          return;
        }
      } else if (roomLower === 'shiv') {
        if (!['Bandish', 'Dazzlers', 'Incredibles'].includes(gname) && !isAdmin) {
          alertService.info('Not authorized', 'Only Bandish, Dazzlers and Incredibles are allowed in Shiv Loka');
          navigate('/gupshup/' + gname);
          return;
        }
      } else if (roomLower === 'bramh') {
        if (!['Singing Stars', 'Sargam', 'Rhythm'].includes(gname) && !isAdmin) {
          alertService.info('Not authorized', 'Only Sargam, Singing Stars and Rhythm are allowed in Brahm Loka');
          navigate('/gupshup/' + gname);
          return;
        }
      }

      socket.emit('joinRoom', { room, userId: currentUser?.SID, picture: currentUser?.picture });

      socket.on('user-joined', (userId) => {
        if (!peerConnections.current[userId]) {
          setupPeerConnection(userId);
        }
      });

      socket.on('roomUsers', (users) => {
        const uniqueUsers = users.filter(
          (user, index, self) => index === self.findIndex(u => u.userId === user.userId)
        );
        setOnlineUsers(uniqueUsers);
      });

      socket.on('message', (newMessage) => {
        setMessages((prevMessages) => [...prevMessages, newMessage].slice(-10));
      });

      socket.on('offer', handleReceiveOffer);
      socket.on('answer', handleReceiveAnswer);
      socket.on('ice-candidate', handleReceiveIceCandidate);

      return () => {
        socket.off('roomUsers');
        socket.off('message');
        socket.off('offer');
        socket.off('answer');
        socket.off('ice-candidate');
        socket.off('user-joined');
      };
    }
  }, [room, currentUser, navigate, socket]);

  // Send message
  const sendMessage = (e) => {
    if (e.key === 'Enter' && newMessage && room) {
      e.preventDefault();
      socket.emit('sendMessage', {
        room,
        message: `${taggedUser} ${newMessage}`.trim(),
        userId: currentUser?.SID,
        name: currentUser?.name,
        picture: currentUser?.picture,
      });
      setNewMessage('');
      setTaggedUser('');
    }
  };

  // WebRTC functions
  const setupPeerConnection = (userId) => {
    const peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });
    peerConnections.current[userId] = peerConnection;

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit('ice-candidate', { target: userId, candidate: event.candidate });
      }
    };

    peerConnection.ontrack = (event) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0];
      }
    };
  };

  const handleReceiveOffer = async ({ offer, fromUserId }) => {
    try {
      const peerConnection = createPeerConnection(fromUserId);
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      socket.emit('answer', { answer, toUserId: fromUserId });
    } catch (error) {
      console.error('Error receiving offer:', error);
    }
  };

  const handleReceiveAnswer = async ({ answer, fromUserId }) => {
    const peerConnection = peerConnections.current[fromUserId];
    if (peerConnection) {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    }
  };

  const handleReceiveIceCandidate = ({ candidate, fromUserId }) => {
    const peerConnection = peerConnections.current[fromUserId];
    if (peerConnection) {
      peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  };

  const createPeerConnection = (userId) => {
    const peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit('ice-candidate', { candidate: event.candidate, toUserId: userId });
      }
    };

    peerConnection.ontrack = (event) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0];
      }
    };

    peerConnections.current[userId] = peerConnection;
    return peerConnection;
  };

  const startVoiceBroadcast = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStream.current = stream;
      if (localAudioRef.current) {
        localAudioRef.current.srcObject = stream;
      }

      for (const userId in peerConnections.current) {
        stream.getTracks().forEach(track => peerConnections.current[userId].addTrack(track, stream));
      }

      for (const userId of Object.keys(peerConnections.current)) {
        const peerConnection = peerConnections.current[userId];
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        socket.emit('offer', { offer, toUserId: userId });
      }
    } catch (error) {
      console.error('Error starting voice broadcast:', error);
    }
  };

  const handleTagUser = (user) => {
    if (user.userId !== currentUser?.SID) {
      setTaggedUser(`@${user.name}`);
    } else {
      setTaggedUser('');
    }
  };

  const getAvatarPosition = (index) => {
    const avatarsPerRow = 5;
    const row = Math.floor(index / avatarsPerRow);
    const col = index % avatarsPerRow;
    const verticalOffset = 70 - (row * 12);
    const horizontalSpacing = 18;
    return {
      top: `${verticalOffset}%`,
      left: `${10 + col * horizontalSpacing}%`,
    };
  };

  const getLokaIcon = () => {
    switch (lokaTheme) {
      case 'vishnu': return '🙏';
      case 'shiv': return '🔱';
      case 'bramh': return '📿';
      default: return '🙏';
    }
  };

  // Get element-specific particle style
  const getParticleStyle = (particle) => {
    const baseStyle = {
      left: `${particle.left}%`,
      top: `${particle.top}%`,
      animationDelay: `${particle.delay}s`,
      animationDuration: `${particle.duration}s`
    };

    switch (particle.type) {
      case 'lotus':
        return { ...baseStyle, fontSize: `${particle.size * 3}px` };
      case 'snow':
        return { ...baseStyle, width: `${particle.size}px`, height: `${particle.size}px`, background: 'white', borderRadius: '50%' };
      case 'stars':
        return { ...baseStyle, fontSize: `${particle.size * 2}px` };
      case 'water':
        return { ...baseStyle, width: `${particle.size * 2}px`, height: `${particle.size}px`, background: 'rgba(100, 200, 255, 0.5)', borderRadius: '50%' };
      default:
        return { ...baseStyle, width: `${particle.size}px`, height: `${particle.size}px` };
    }
  };

  const getParticleContent = (type) => {
    switch (type) {
      case 'lotus': return '🪷';
      case 'stars': return '✨';
      case 'flowers': return '🌸';
      case 'swans': return '🦢';
      case 'butterflies': return '🦋';
      case 'feathers': return '🪶';
      case 'scrolls': return '📜';
      case 'om': return 'ॐ';
      default: return '';
    }
  };

  return (
    <Fragment>
      <div
        className={`main-content virtual-world loka-${lokaTheme} ${isTransitioning ? 'transitioning' : ''}`}
        style={{ backgroundImage: `url('${currentLocation?.background}')` }}
        onClick={enableAudio}
      >
        {/* Audio elements */}
        <audio ref={ambientAudioRef} />
        <audio ref={bellAudioRef} />
        <audio ref={transitionAudioRef} />
        <audio ref={localAudioRef} autoPlay muted />
        <audio ref={remoteAudioRef} autoPlay />

        {/* Divine overlay */}
        <div className="divine-overlay" />

        {/* Particles/Elements */}
        <div className="particles-container">
          {particles.map(particle => (
            <div
              key={particle.id}
              className={`divine-particle ${particle.type}`}
              style={getParticleStyle(particle)}
            >
              {getParticleContent(particle.type)}
            </div>
          ))}
        </div>

        {/* Header with location name */}
        <div className="loka-header">
          <div className="location-title">
            <span className="loka-icon">{getLokaIcon()}</span>
            <h2 className="loka-title">{currentLocation?.name}</h2>
            <span className="loka-icon">{getLokaIcon()}</span>
          </div>
          <div className="location-subtitle">{currentLocation?.nameEn}</div>
        </div>

        {/* Location info popup */}
        {showLocationInfo && (
          <div className="location-info-popup">
            <div className="location-info-title">{currentLocation?.name}</div>
            <div className="location-info-desc">{currentLocation?.description}</div>
            <div className="location-info-blessing">{currentLocation?.blessing}</div>
          </div>
        )}

        {/* Location dots (mini-map) */}
        <div className="location-dots">
          {locations.map((loc, index) => (
            <button
              key={loc.id}
              className={`location-dot ${index === currentLocationIndex ? 'active' : ''}`}
              onClick={() => goToLocation(index)}
              title={loc.name}
            >
              <span className="dot-icon">
                {index === currentLocationIndex ? '🔆' : '○'}
              </span>
              <span className="dot-name">{loc.name}</span>
            </button>
          ))}
        </div>

        {/* Deity blessing bubble */}
        {showBlessings && (
          <div className="deity-blessing">
            <div className="blessing-text">{currentBlessing}</div>
          </div>
        )}

        {/* Interactive elements panel */}
        <div className="interactions-panel">
          <button
            className={`interaction-btn ${bellRinging ? 'ringing' : ''}`}
            onClick={ringBell}
            title="Ring Bell"
          >
            <span className="icon">🔔</span>
            <span className="label">घंटी</span>
          </button>
          <button
            className={`interaction-btn ${diyaLit ? 'lit' : ''}`}
            onClick={lightDiya}
            title="Light Diya"
          >
            <span className="icon">🪔</span>
            <span className="label">दीया</span>
          </button>
          <button
            className="interaction-btn"
            onClick={offerFlowers}
            title="Offer Flowers"
          >
            <span className="icon">🌺</span>
            <span className="label">पुष्प</span>
            {flowersOffered > 0 && <span className="flower-count">{flowersOffered}</span>}
          </button>
          <button
            className="interaction-btn"
            onClick={doPranam}
            title="Pranam"
          >
            <span className="icon">🙏</span>
            <span className="label">प्रणाम</span>
          </button>
        </div>

        {/* Audio toggle hint */}
        {!audioEnabled && (
          <div className="audio-prompt">
            🔊 कहीं भी छूएं दिव्य ध्वनि के लिए
          </div>
        )}

        {/* Users in the loka */}
        {onlineUsers.map((user, index) => (
          user?.userId && user?.picture && (
            <div
              key={user?.userId}
              className="avatar-container"
              style={getAvatarPosition(index)}
              onClick={() => handleTagUser(user)}
            >
              <div
                className="avatarlok"
                style={{ backgroundImage: `url(${cdn + 'users/' + user?.picture})` }}
              />

              {messages
                .filter((msg) => msg.userId === user?.userId)
                .slice(-1)
                .map((msg, idx) => (
                  <div key={idx} className={`chat-bubble ${msg.isAction ? 'action-bubble' : ''}`}>
                    <span className="message-sender">{msg.name}:</span>
                    <span className="message-content">{msg.message}</span>
                  </div>
                ))}

              {user.userId === currentUser?.SID && (
                <div className='input-container'>
                  <input
                    type="text"
                    className="message-input-below-avatar"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={sendMessage}
                    placeholder={taggedUser ? `Reply to ${taggedUser}` : 'संदेश लिखें...'}
                  />
                  <i
                    onClick={startVoiceBroadcast}
                    className="font-xsss text-white text-center feather-mic btn-round-sm bg-gold-gradiant me-1 counter"
                  />
                </div>
              )}
            </div>
          )
        ))}

        {/* Footer with location count */}
        <div className="loka-footer">
          <span className="online-count">
            👥 {onlineUsers.length} भक्तजन
          </span>
          <span className="location-indicator">
            📍 {currentLocationIndex + 1} / {locations.length}
          </span>
        </div>
      </div>
    </Fragment>
  );
}
